import { useSelector } from 'react-redux';
import { Modifier } from '@pizza-hut-us-development/client-core';
import { useDecision } from '@optimizely/react-sdk';
import { useCCGetDealBuilderQuery } from '@/clientCore/temporaryTransformationalHooks/useCCGetDealBuilderQuery';
import { selectors } from '@/builders/pizza/slice/pizza.slice';
import { getCrustUpchargeAmount } from '@/clientCore/menu/utils/pizzaPrices';
import { dealSelectors } from '@/builders/deals/slice/deal.slice';
import Routes from '@/router/routes';
import { useCCGetPizzaBuilderQuery } from '@/clientCore/temporaryTransformationalHooks/useCCGetPizzaBuilderQuery';

interface PizzaPricing {
  crustUpcharges?: { upcharge: number; id: string }[] | undefined;
  toppingUpChargePrice: number;
  numberOfIncludedToppings: number;
}

const getToppingUpchargePrice = (selectedCrustOption: Modifier) => {
  const toppingUpchargeLimits = selectedCrustOption?.upcharges
    ?.find((toppingUpcharges) => toppingUpcharges.slotCode.toLowerCase().includes('topping'))
    ?.weightPriceRanges;

  const toppingUpChargePrice = toppingUpchargeLimits?.reduce((upCharge, priceRange) => {
    if (upCharge < priceRange.price.amount) {
      return priceRange.price.amount;
    }

    return upCharge;
  }, 0);

  return toppingUpChargePrice;
};

const getNumberOfIncludedToppings = (selectedCrustOption: Modifier | undefined) => selectedCrustOption?.upcharges
  ?.find((upcharge) => upcharge?.slotCode.toLowerCase().includes('topping'))
  ?.weightPriceRanges
  .find((range) => range?.price?.amount === 0)?.rangeEnd;

const usePizzaDealPricing = ({ skip = false }: { skip?: boolean}): PizzaPricing => {
  const pizzaId = useSelector(selectors.selectPizzaId);
  const deal = useSelector(dealSelectors.selectDeal);
  const selectedSize = useSelector(selectors.selectPizzaSizeOption);
  const selectedCrust = useSelector(selectors.selectPizzaCrust);
  const { rawData } = useCCGetDealBuilderQuery({ itemId: deal?.data.id, skip });

  if (!rawData || skip) {
    return {
      crustUpcharges: undefined,
      toppingUpChargePrice: 0,
      numberOfIncludedToppings: 0
    };
  }

  const selectedRecipe = rawData?.steps[0].recipes.find((recipe) => recipe.id === pizzaId);
  const selectedSizeOption = selectedRecipe?.options
    .find((findSize) => findSize.id.toLowerCase().includes('size'))
    ?.modifiers?.find((size) => size.id === selectedSize.id);
  const crustUpcharges = selectedSizeOption?.modifiers?.map((crust) => ({
    upcharge: selectedRecipe?.options ? getCrustUpchargeAmount(crust, selectedRecipe.options) : 0,
    id: crust.id
  }));
  const selectedCrustOption = selectedSizeOption?.modifiers?.find((crust) => crust.id === selectedCrust?.id);
  const toppingUpChargePrice = selectedCrustOption ? getToppingUpchargePrice(selectedCrustOption) : 0;

  return {
    crustUpcharges,
    toppingUpChargePrice: toppingUpChargePrice || 0,
    numberOfIncludedToppings: getNumberOfIncludedToppings(selectedCrustOption) || 0
  };
};

const usePizzaMenuPricing = ({ skip = false }: { skip?: boolean}): PizzaPricing => {
  const pizzaId = useSelector(selectors.selectPizzaId);
  const selectedCrust = useSelector(selectors.selectPizzaCrust);
  const { rawData } = useCCGetPizzaBuilderQuery({ itemId: pizzaId, skip });

  if (!rawData || skip) {
    return {
      toppingUpChargePrice: 0,
      numberOfIncludedToppings: 0
    };
  }
  const selectedCrustOption = rawData?.sizesAndCrusts
    .flatMap((size) => size?.modifiers)
    .find((crust) => crust?.variantCode === selectedCrust?.variantCode);
  const toppingUpChargePrice = selectedCrustOption ? getToppingUpchargePrice(selectedCrustOption) : 0;

  return {
    toppingUpChargePrice: toppingUpChargePrice || 0,
    numberOfIncludedToppings: getNumberOfIncludedToppings(selectedCrustOption) || 0
  };
};

export const usePizzaPricing = (currentPath?: string): PizzaPricing => {
  const [{ enabled: isToppingPriceEditFixEnabled }] = useDecision('dtg-1824-topping-price-editing-fix');
  const deal = useSelector(dealSelectors.selectDeal);
  const isDealContext = !!deal?.data?.id;
  const isDealsFlow = isToppingPriceEditFixEnabled
    ? currentPath?.includes(Routes.DEALS) || isDealContext
    : currentPath?.includes(Routes.DEALS);
  const pizzaDealPricing = usePizzaDealPricing({ skip: !isDealsFlow });
  const pizzaMenuPricing = usePizzaMenuPricing({ skip: isDealsFlow });
  return isDealsFlow ? pizzaDealPricing : pizzaMenuPricing;
};
